import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch, store } from 'store';
import { WhatsappEditTemplate, WhatsappOriginalTemplate, WhatsappTemplate } from 'types/templates';
import { fetcher } from 'utils/axios';
import { fetchTemplateDefaultFiles, type TemplateDefaultFile } from 'api/template-default-files';

const initialState: {
  whatsappTemplates: WhatsappTemplate[];
  whatsappOriginalTemplates: WhatsappOriginalTemplate[];
  languages: string[];
  /** true until the first GetWhatsappTemplates fetch completes (or after reset) */
  whatsappTemplatesLoading: boolean;
  /** קובץ ברירת מחדל לכותרת מדיה פר תבנית — מפתח "name::language" (language ריק = כל השפות) */
  defaultFiles: Record<string, { fileUrl: string; fileName: string | null }>;
  errorPopup: {
    show: boolean;
    template: WhatsappEditTemplate | null;
  };
} = {
  whatsappTemplates: [],
  whatsappOriginalTemplates: [],
  languages: [],
  whatsappTemplatesLoading: true,
  defaultFiles: {},
  errorPopup: {
    show: false,
    template: null,
  },
};

export function templateDefaultFileKey(templateName: string, language?: string | null): string {
  return `${String(templateName || '').toLowerCase()}::${String(language || '').toLowerCase()}`;
}

/** שליפת ברירת המחדל לתבנית: קודם התאמת שם+שפה, אחרת רשומת השם בלי שפה */
export function getTemplateDefaultFile(
  defaultFiles: Record<string, { fileUrl: string; fileName: string | null }>,
  templateName: string | null | undefined,
  language?: string | null,
): { fileUrl: string; fileName: string | null } | null {
  if (!templateName) return null;
  return (
    defaultFiles[templateDefaultFileKey(templateName, language)] ??
    defaultFiles[templateDefaultFileKey(templateName, '')] ??
    null
  );
}

const TEMPLATES_CACHE_KEY = 'templates.whatsapp.cache.v1';
const TEMPLATES_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h
const MIN_REFETCH_INTERVAL_MS = 60 * 1000; // אל תירה בקשה מחדש כל פתיחה מהירה של פאנל

let templatesFetchInFlight: Promise<void> | null = null;
let templatesLastFetchStartedAt = 0;

type TemplatesPayload = {
  whatsappTemplates: WhatsappTemplate[];
  whatsappOriginalTemplates: WhatsappOriginalTemplate[];
  languages: string[];
};

function normalizeTemplatesPayload(data: any): TemplatesPayload {
  return {
    whatsappTemplates: Array.isArray(data?.whatsappTemplates) ? data.whatsappTemplates : [],
    whatsappOriginalTemplates: Array.isArray(data?.whatsappOriginalTemplates)
      ? data.whatsappOriginalTemplates
      : [],
    languages: Array.isArray(data?.languages) ? data.languages : [],
  };
}

function hasAnyTemplates(payload: Partial<TemplatesPayload> | null | undefined): boolean {
  if (!payload) return false;
  return (
    (Array.isArray(payload.whatsappTemplates) && payload.whatsappTemplates.length > 0) ||
    (Array.isArray(payload.whatsappOriginalTemplates) && payload.whatsappOriginalTemplates.length > 0)
  );
}

function readTemplatesCache(): TemplatesPayload | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(TEMPLATES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const cachedAt = Number(parsed?.cachedAt);
    if (!Number.isFinite(cachedAt)) return null;
    if (Date.now() - cachedAt > TEMPLATES_CACHE_TTL_MS) return null;
    return normalizeTemplatesPayload(parsed?.data);
  } catch {
    return null;
  }
}

function writeTemplatesCache(payload: TemplatesPayload) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(
      TEMPLATES_CACHE_KEY,
      JSON.stringify({
        cachedAt: Date.now(),
        data: payload,
      }),
    );
  } catch {
    /* ignore cache write failures */
  }
}

async function fetchWhatsappTemplatesFromServer(): Promise<TemplatesPayload | null> {
  const data = await fetcher('get', '/api/services/GetWhatsappTemplates', 'newapi');
  if (!data) return null;
  return normalizeTemplatesPayload(data);
}

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    setWhatsappTemplates(state, action: PayloadAction<WhatsappTemplate[]>) {
      state.whatsappTemplates = action.payload;
    },
    setWhatsappOriginalTemplates(state, action: PayloadAction<WhatsappOriginalTemplate[]>) {
      state.whatsappOriginalTemplates = action.payload;
    },
    storeDeleteTemplate(state, action: PayloadAction<{ id: string; name: string }>) {
      const { id, name } = action.payload;
      const index = state.whatsappTemplates.findIndex((template) => template.id === id);
      if (index !== -1) {
        state.whatsappTemplates.splice(index, 1);
      }
      const indexOriginal = state.whatsappOriginalTemplates.findIndex((template) => template.name === name);
      if (indexOriginal !== -1) {
        state.whatsappOriginalTemplates.splice(indexOriginal, 1);
      }
    },
    setAllTemplates(
      state,
      action: PayloadAction<{
        whatsappTemplates: WhatsappTemplate[];
        whatsappOriginalTemplates: WhatsappOriginalTemplate[];
        languages: string[];
      }>,
    ) {
      state.whatsappTemplates = action.payload.whatsappTemplates;
      state.whatsappOriginalTemplates = action.payload.whatsappOriginalTemplates;
      state.languages = action.payload.languages;
    },
    resetTemplates(state) {
      return initialState;
    },
    updateErrorPopup(
      state,
      action: PayloadAction<{ show?: boolean; template?: WhatsappEditTemplate | null }>,
    ) {
      Object.assign(state.errorPopup, action.payload);
    },
    setWhatsappTemplatesLoading(state, action: PayloadAction<boolean>) {
      state.whatsappTemplatesLoading = action.payload;
    },
    setTemplateDefaultFiles(state, action: PayloadAction<TemplateDefaultFile[]>) {
      const map: Record<string, { fileUrl: string; fileName: string | null }> = {};
      for (const row of action.payload) {
        map[templateDefaultFileKey(row.templateName, row.language)] = {
          fileUrl: row.fileUrl,
          fileName: row.fileName ?? null,
        };
      }
      state.defaultFiles = map;
    },
  },
});

export default templatesSlice.reducer;
export const {
  setWhatsappTemplates,
  setWhatsappOriginalTemplates,
  setAllTemplates,
  storeDeleteTemplate,
  resetTemplates,
  updateErrorPopup,
  setWhatsappTemplatesLoading,
  setTemplateDefaultFiles,
} = templatesSlice.actions;

/** טעינת קבצי ברירת המחדל מהנוד — fail-soft: כשל משאיר את המפה כפי שהיא */
export async function loadTemplateDefaultFiles() {
  try {
    const rows = await fetchTemplateDefaultFiles();
    dispatch(setTemplateDefaultFiles(rows));
  } catch (error) {
    console.warn('loadTemplateDefaultFiles err: ', error);
  }
}

export async function getWhatsappTemplates(
  options?: { force?: boolean; allowCache?: boolean },
) {
  const force = options?.force === true;
  const allowCache = options?.allowCache !== false;
  const state = store.getState().templates;
  const hasStateData = hasAnyTemplates({
    whatsappTemplates: state.whatsappTemplates,
    whatsappOriginalTemplates: state.whatsappOriginalTemplates,
  });

  if (allowCache && !hasStateData) {
    const cached = readTemplatesCache();
    if (cached && hasAnyTemplates(cached)) {
      dispatch(setAllTemplates(cached));
      dispatch(setWhatsappTemplatesLoading(false));
    }
  }

  const now = Date.now();
  const hasDataAfterCache = hasAnyTemplates({
    whatsappTemplates: store.getState().templates.whatsappTemplates,
    whatsappOriginalTemplates: store.getState().templates.whatsappOriginalTemplates,
  });

  if (!force && hasDataAfterCache && now - templatesLastFetchStartedAt < MIN_REFETCH_INTERVAL_MS) {
    return;
  }

  if (templatesFetchInFlight) return templatesFetchInFlight;

  if (!hasDataAfterCache) {
    dispatch(setWhatsappTemplatesLoading(true));
  } else {
    dispatch(setWhatsappTemplatesLoading(false));
  }

  templatesLastFetchStartedAt = now;
  //* קבצי ברירת המחדל נטענים במקביל לרשימה — לא חוסמים ולא מפילים אותה
  void loadTemplateDefaultFiles();
  templatesFetchInFlight = (async () => {
    try {
      const payload = await fetchWhatsappTemplatesFromServer();
      if (payload) {
        dispatch(setAllTemplates(payload));
        writeTemplatesCache(payload);
      }
    } catch (error) {
      console.warn('getCompanyData err: ', error);
    } finally {
      dispatch(setWhatsappTemplatesLoading(false));
      templatesFetchInFlight = null;
    }
  })();

  return templatesFetchInFlight;
}

/** רענון רשימת תבניות בלי מצב טעינה גלובלי של הגריד — לסנכרון רקע (Template Watcher) */
export async function refreshWhatsappTemplatesSilently(): Promise<boolean> {
  try {
    const payload = await fetchWhatsappTemplatesFromServer();
    if (payload) {
      dispatch(setAllTemplates(payload));
      writeTemplatesCache(payload);
      return true;
    }
    return false;
  } catch (error) {
    console.warn('refreshWhatsappTemplatesSilently err: ', error);
    return false;
  }
}
export async function fetchDeleteWhatsappTemplate(id: string, name: string) {
  try {
    // Align with create/edit template: same newapi host + Meta token/WABA context (not `history` app host).
    const data = await fetcher(
      'post',
      [
        '/api/wa-business/deleteTemplate',
        {
          data: {
            name: name,
            hsm_id: id,
          },
        },
      ],
      'newapi',
    );

    if (data?.result) {
      // await getWhatsappTemplates();
      dispatch(storeDeleteTemplate({ id, name }));
    }

    return data || null;
  } catch (error) {
    console.warn('fetchDeleteWhatsappTemplate err: ', error);
    return null;
  }
}
export function handleErrorPopup(params: { show?: boolean; template?: WhatsappEditTemplate | null }) {
  dispatch(updateErrorPopup({ ...params }));
}
